import api from './api'

export type AppointmentStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'RESCHEDULED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'EXPIRED'

export type AppointmentModality = 'IN_PERSON' | 'REMOTE'

export interface Appointment {
  id: string
  tenantId: string
  conversationId: string | null
  contactId: string | null
  waContact: string | null
  patientName: string | null
  serviceId: string | null
  serviceName: string | null
  modality: AppointmentModality
  specialty: string | null
  requestedSlotText: string
  requestedSlotStart: string | null
  requestedSlotEnd: string | null
  confirmedSlotStart: string | null
  confirmedSlotEnd: string | null
  staffUserId: string | null
  locationOrRoom: string | null
  status: AppointmentStatus
  notes: string | null
  confirmedBy: string | null
  confirmedAt: string | null
  cancelledBy: string | null
  cancelledAt: string | null
  cancelReason: string | null
  expiresAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AppointmentListFilters {
  status?: AppointmentStatus | 'all'
  conversationId?: string
  page?: number
  limit?: number
}

export interface AppointmentPagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface ConfirmAppointmentPayload {
  confirmedSlotStart: string
  confirmedSlotEnd: string
  staffUserId: string
  locationOrRoom?: string
  customMessage?: string
}

export interface RescheduleAppointmentPayload {
  newRequestedSlotText: string
  newRequestedSlotStart?: string
  staffUserId: string
  customMessage?: string
}

export interface CancelAppointmentPayload {
  cancelledBy: string
  cancelReason?: string
  customMessage?: string
  notifyPatient?: boolean
}

export interface StaffUser {
  id: string
  name: string | null
  email: string
}

export interface AvailabilitySlot {
  start: string
  end: string
  available: boolean
  conflictingAppointmentId?: string
}

export interface StaffAvailability {
  slots: AvailabilitySlot[]
  dayEnabled: boolean
  schedule: { enabled: boolean; startTime: string; endTime: string } | null
}

function buildQuery(filters: AppointmentListFilters): string {
  const params = new URLSearchParams()
  if (filters.status && filters.status !== 'all') params.set('status', filters.status)
  if (filters.conversationId) params.set('conversationId', filters.conversationId)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  const query = params.toString()
  return query ? `?${query}` : ''
}

export const appointmentsApi = {
  async getAppointments(filters: AppointmentListFilters = {}): Promise<{
    appointments: Appointment[]
    pagination: AppointmentPagination
  }> {
    const response = await api.get(`/appointments${buildQuery(filters)}`)
    return {
      appointments: (response.data?.data ?? []) as Appointment[],
      pagination: (response.data?.pagination ?? {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
      }) as AppointmentPagination,
    }
  },

  async getPendingCount(): Promise<number> {
    const response = await api.get('/appointments/pending-count')
    return Number(response.data?.data?.count ?? 0)
  },

  async getStaffAvailability(staffUserId: string, date: string): Promise<StaffAvailability> {
    const response = await api.get('/appointments/availability', {
      params: { staffUserId, date },
    })
    return (
      response.data?.data ?? {
        slots: [],
        dayEnabled: false,
        schedule: null,
      }
    )
  },

  async confirmAppointment(id: string, payload: ConfirmAppointmentPayload): Promise<Appointment> {
    const response = await api.put(`/appointments/${id}/confirm`, payload)
    return response.data?.data as Appointment
  },

  async rescheduleAppointment(
    id: string,
    payload: RescheduleAppointmentPayload
  ): Promise<{ previous: Appointment; next: Appointment }> {
    const response = await api.put(`/appointments/${id}/reschedule`, payload)
    return response.data?.data as { previous: Appointment; next: Appointment }
  },

  async cancelAppointment(id: string, payload: CancelAppointmentPayload): Promise<Appointment> {
    const response = await api.put(`/appointments/${id}/cancel`, payload)
    return response.data?.data as Appointment
  },
}
